import React from 'react';

import { Admin, Resource } from 'react-admin';
import PageViewIcon from '@material-ui/icons/Pageview';
import PostIcon from '@material-ui/icons/Book';
import UserIcon from '@material-ui/icons/Group';
import TopicsIcon from '@material-ui/icons/ChromeReaderMode';
import PageIcon from '@material-ui/icons/Description';
import JssProvider from 'react-jss/lib/JssProvider';

import { Dashboard } from './dashboard';

import { MediasourceList, MediasourceEdit, MediasourceCreate } from './resources/mediasources';
import { MediaTypesList, MediaTypesEdit, MediaTypesCreate } from './resources/mediatypes';
import { ClusterList, ClusterDetails } from './resources/clusters';
import { CategoryList, CategoryEdit } from './resources/categories';
import { TopicList, TopicEdit, TopicCreate } from './resources/topics';
import { TopicStatsList } from './resources/topicsstats';
import { TwitterUserList, TwitterUserEdit, TwitterUserCreate } from './resources/twitterusers';
import { TwitterUserNewList, TwitterUserNewShow } from './resources/twitterusersnew';
import { FacebookPostsList, FacebookPostEdit } from './resources/facebookposts';
import { FacebookPagesList, FacebookPageEdit, FacebookPageCreate } from './resources/facebookpages';
import { ArticlesReportList, ArticlesReportDetails } from './resources/articlesreport';
import { ArticlesList, ArticleDetails, ArticleEdit } from './resources/articles';
import { TweetsList, TweetEdit } from './resources/tweets';
import { SystemLogsList, SystemLogShow } from './resources/systemlogs';
import { UsersList, UserEdit, UserCreate } from './resources/users';
import { Notification } from './notifications';
import { YoutubeList, YoutubeEdit, YoutubeCreate } from './resources/youtube';
import { AutopostcategoriesList, AutopostcategoriesEdit, AutopostcategoriesCreate } from './resources/autopostcategories';
import { KeywordWeightList, KeywordWeightEdit, KeywordWeightCreate } from './resources/keywordweights';
import { ConfigEdit } from './resources/config';


import clustersSaga from './resources/clustersSaga';
import authClient from './services/authClient';
import restClient from './services/restClient';
import addUploadCapabilities from './services/addUploadCapabilities';
import languageReducer from './reducers/languageReducer';

import Layout from './Layout';
import { appOptions } from './config/app';

const escapeRegex = /([[\].#*$><+~=|^:(),"'`\s])/g;
let classCounter = 0;

// Heavily inspired of Material UI:
// @see https://github.com/mui-org/material-ui/blob/9cf73828e523451de456ba3dbf2ab15f87cf8504/src/styles/createGenerateClassName.js
// The issue with the MUI function is that is create a new index for each
// new `withStyles`, so we handle have to write our own counter
export const generateClassName = (rule, styleSheet) => {
  classCounter += 1;

  if (process.env.NODE_ENV === 'production') {
      return `c${classCounter}`;
  }

  if (styleSheet && styleSheet.options.classNamePrefix) {
      let prefix = styleSheet.options.classNamePrefix;
      // Sanitize the string as will be used to prefix the generated class name.
      prefix = prefix.replace(escapeRegex, '-');

      if (prefix.match(/^Mui/)) {
          return `${prefix}-${rule.key}`;
      }

      return `${prefix}-${rule.key}-${classCounter}`;
  }

  return `${rule.key}-${classCounter}`;
};


const adminResources = [
  <Resource
    key="mediasources"
    name="mediasources"
    options={{ label: 'Media Sources' }}
    list={MediasourceList}
    edit={MediasourceEdit}
    create={MediasourceCreate}
  />,
  <Resource key="notifications" name="notifications" options={{ label: 'Notifications' }} list={Notification} />,
  <Resource
    key="mediatypes"
    name="mediatypes"
    options={{ label: 'Media Types' }}
    list={MediaTypesList}
    edit={MediaTypesEdit}
    create={MediaTypesCreate}
  />,
  <Resource
    key="articles/report"
    name="articles/report"
    options={{ label: 'Articles Report' }}
    list={ArticlesReportList}
    edit={ArticlesReportDetails}
  />,
  <Resource
    key="articles/categories"
    name="articles/categories"
    options={{ label: 'Categories' }}
    list={CategoryList}
    edit={CategoryEdit}
  />,
  <Resource
    key="articles"
    name="articles"
    options={{ show_tags: true }}
    icon={PageViewIcon}
    list={ArticlesList}
    edit={ArticleEdit}
    show={ArticleDetails}
  />,
  <Resource
    key="topics/report"
    name="topics/report"
    list={TopicStatsList}
    edit={TopicEdit}
    icon={TopicsIcon}
  />,
  <Resource
    key="topics"
    name="topics"
    list={TopicList}
    edit={TopicEdit}
    create={TopicCreate}
    icon={TopicsIcon}
  />,
  <Resource key="clustersadmin" name="clustersadmin" list={ClusterList} icon={PostIcon} edit={ClusterDetails} />,
  <Resource key="tweets" name="tweets" list={TweetsList} show={TweetEdit} />,
  <Resource
    key="twitterusers"
    name="twitterusers"
    options={{ label: 'Twitter Users' }}
    list={TwitterUserList}
    edit={TwitterUserEdit}
    create={TwitterUserCreate}
    icon={UserIcon}
  />,
  <Resource
    key="twitterusersnew"
    name="twitterusersnew"
    options={{ label: 'New Twitter Users' }}
    list={TwitterUserNewList}
    show={TwitterUserNewShow}
    icon={UserIcon}
  />,
  <Resource
    key="facebookpages"
    name="facebookpages"
    options={{ label: 'Facebook Pages' }}
    list={FacebookPagesList}
    edit={FacebookPageEdit}
    create={FacebookPageCreate}
  />,
  <Resource
    key="facebookposts"
    name="facebookposts"
    options={{ label: 'Facebook Posts' }}
    list={FacebookPostsList}
    edit={FacebookPostEdit}
  />,
  <Resource
    key="systemlogs"
    name="systemlogs"
    options={{ label: 'System Logs' }}
    list={SystemLogsList}
    show={SystemLogShow}
    icon={PageIcon}
  />,
  <Resource key="users" name="users" list={UsersList} edit={UserEdit} create={UserCreate} />,
  <Resource
    key="youtube"
    name="youtube"
    list={YoutubeList}
    edit={YoutubeEdit}
    create={YoutubeCreate}
  />,
  <Resource
    key="config/app"
    name="config/app"
    edit={ConfigEdit}
  />,
  <Resource
    key="autopost/categories"
    name="autopost/categories"
    list={AutopostcategoriesList}
    edit={AutopostcategoriesEdit}
    create={AutopostcategoriesCreate}
  />,
  <Resource
    key="keywordweights"
    name="keywordweights"
    list={KeywordWeightList}
    edit={KeywordWeightEdit}
    create={KeywordWeightCreate}
  />,
  <Resource key="config/roles" name="config/roles" />,
  <Resource key="config/languages" name="config/languages" />,
  <Resource key="mediasources/types" name="mediasources/types" />,
  <Resource key="articles/categories-choice" name="articles/categories-choice" />,
];

const App = () => (
  <JssProvider generateClassName={generateClassName}>
    <Admin
      appLayout={Layout}
      title="Grid Admin"
      dashboard={Dashboard}
      customSagas={[clustersSaga]}
      authProvider={authClient(appOptions)}
      dataProvider={addUploadCapabilities(restClient(appOptions))}
      customReducers={{ language: languageReducer }}
    >
      {adminResources}
    </Admin>
  </JssProvider>
);

export default App;



// WEBPACK FOOTER //
// ./src/App.js